import * as Three from 'three';
import store from '../../redux/store';
import { updateActiveComponent, setSelectedLightId } from '../../redux/Slice/globalStates';
import { selected_object, setMaterialHighlightMeshes } from '../../redux/Slice/gridSlice';
import {
  clearMultiSelection,
  toggleSceneObjectSelection,
} from '../selectAllObjects';
import { pickClosestPlyFromRay } from './meshRaycastUtils';
import {
  isParticleLifeEnded,
  resolveParticleAnimationRoot,
} from '../particles/particleAnimationUtils';
import {
  isSceneCameraOrPoint,
  isSceneCameraPoint,
  resolveSceneCameraFromPoint,
} from './sceneCameraUtils';
import { syncSelectionOutlineFromStore } from './selectionOutline';
import { clampSelectionForChildMode } from '../../components/gridpagefunction/MeshExtractation';
import {
  findAreaLightFromIntersects,
  findDirectionalLightFromIntersects,
  findPointLightFromIntersects,
  findSpotLightFromIntersects,
} from '../../components/layout/gridpage/GridPageFunctions';
import { hasActivePivotSession } from './pivotEditor.js';

function findTopLevelSelectableObject(obj, selectableObjects) {
  if (!obj) return null;
  let current = obj;
  while (current) {
    if (selectableObjects.includes(current)) return current;
    current = current.parent;
  }
  return null;
}

function isDescendantOfObject(obj, ancestor) {
  if (!obj || !ancestor) return false;
  let current = obj;
  while (current) {
    if (current === ancestor) return true;
    current = current.parent;
  }
  return false;
}

/**
 * Resolve a viewport/hierarchy pick to the editor-selectable root.
 * Particle systems always select the root group (never an inner sphere/cluster).
 * GLB picks respect child-selection mode.
 */
export function resolveEditorSelectableObject(
  object,
  scene = null,
  activeParentForChildSelection = null
) {
  if (!object) return null;

  const particleRoot = resolveParticleAnimationRoot(object);
  if (particleRoot) return particleRoot;

  if (!scene) return object;
  return clampSelectionForChildMode(
    scene,
    object,
    activeParentForChildSelection
  );
}

/** Viewport pick while child-selection mode is on for a GLB parent. */
export function resolveSelectionWithChildMode(
  intersected,
  selectableObjects,
  activeParentForChildSelection,
  scene = null
) {
  if (!intersected) return null;

  const parent = activeParentForChildSelection;

  // Clouds/rain/etc.: ray hits an inner mesh — always promote to particle root.
  const particleRoot = resolveParticleAnimationRoot(intersected);
  if (particleRoot) return particleRoot;

  if (parent && isDescendantOfObject(intersected, parent)) {
    return scene
      ? clampSelectionForChildMode(scene, intersected, parent)
      : intersected;
  }

  if (scene) {
    const glbClamped = clampSelectionForChildMode(scene, intersected, null);
    // Only short-circuit when clamp actually promoted to a GLB pivot.
    if (glbClamped && glbClamped !== intersected) {
      return glbClamped;
    }
  }

  const topLevel = findTopLevelSelectableObject(
    intersected,
    selectableObjects
  );
  if (topLevel) {
    return resolveEditorSelectableObject(topLevel, scene, parent);
  }

  if (parent) return null;
  return intersected;
}

function resolveActualLightFromPick(topLevel, scene) {
  if (!topLevel || !scene) return null;

  if (topLevel.userData?.isEditorLightIcon) {
    const lightId =
      topLevel.userData?.lightId ||
      (typeof topLevel.name === 'string'
        ? topLevel.name.replace(/-light-icon$/, '')
        : null);
    if (!lightId) return null;
    const lightObj = scene.getObjectByName(lightId);
    if (lightObj?.isLight) return lightObj;
  }

  if (topLevel.type === 'DirectionalLightHelper') {
    return findDirectionalLightFromIntersects(topLevel, scene);
  }
  if (topLevel.type === 'PointLightHelper') {
    return findPointLightFromIntersects(topLevel, scene);
  }
  if (topLevel.type === 'SpotLightHelper') {
    return findSpotLightFromIntersects(topLevel, scene);
  }
  if (topLevel.type === 'AreaLightHelper') {
    return findAreaLightFromIntersects(topLevel, scene);
  }
  if (topLevel.type === 'AmbientLightHelper') {
    return findAreaLightFromIntersects(topLevel, scene);
  }

  return null;
}

function isLightPick(topLevel, actualLight) {
  if (actualLight) return true;
  return (
    topLevel?.type === 'DirectionalLight' ||
    topLevel?.type === 'PointLight' ||
    topLevel?.type === 'SpotLight' ||
    topLevel?.type === 'AmbientLight' ||
    topLevel?.type === 'RectAreaLight'
  );
}

function clearLegacyGridObjectListHighlight() {
  const addImageContainer = document.getElementById('grid_objects_list');
  if (!addImageContainer) return;
  const otherChildren = addImageContainer.children;
  for (let i = 0; i < otherChildren.length; i++) {
    otherChildren[i].style.backgroundColor = '';
  }
}

function buildSelectableRaycastTargets(allObjects) {
  const filtered = allObjects.filter(
    (obj) =>
      obj.type !== 'CameraHelper' &&
      obj.name !== 'DemoCameraHelper' &&
      obj.name !== 'hdriBackgroundSphere' &&
      !obj.userData?.isHdriBackground &&
      obj.visible !== false &&
      !isParticleLifeEnded(obj)
  );

  // Raycast roots only with recursive:true — flattening every descendant
  // allocated a huge array on each click and froze dense scenes.
  return { filtered, allObjectsWithChildren: filtered };
}

function syncCameraHelperState(
  syncCameraHelperVisibility,
  showCameraHelper,
  selectedCameraId = null,
  hideAll = false
) {
  if (typeof syncCameraHelperVisibility === 'function') {
    syncCameraHelperVisibility(selectedCameraId, hideAll);
    return;
  }

  if (!selectedCameraId || hideAll) {
    showCameraHelper?.(false);
  }
}

export function pickViewportObject({ mouse, raycaster, camera, allObjects, scene }) {
  const { filtered, allObjectsWithChildren } =
    buildSelectableRaycastTargets(allObjects);

  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(allObjectsWithChildren, true);

  let pickedObject = null;
  let pickedDistance = Infinity;

  for (const hit of intersects) {
    if (isParticleLifeEnded(hit.object)) continue;
    pickedObject = hit.object;
    pickedDistance = hit.distance;
    break;
  }

  const plyPick = pickClosestPlyFromRay(raycaster.ray, filtered);
  if (plyPick && plyPick.distance < pickedDistance) {
    if (!isParticleLifeEnded(plyPick.object)) {
      pickedObject = plyPick.object;
      pickedDistance = plyPick.distance;
    }
  }

  return pickedObject;
}

function handleCameraPick({
  intersected,
  isMultiToggle,
  scene,
  transformControls,
  currentCamera,
  setSelectedObject,
  setSelectedObjectState,
  UpdateUI,
  cachedUIElements,
  showCameraHelper,
  syncCameraHelperVisibility,
}) {
  const sceneCam = resolveSceneCameraFromPoint(scene, intersected);
  if (!sceneCam || !transformControls) return false;

  if (isMultiToggle) {
    toggleSceneObjectSelection({
      scene,
      object: sceneCam,
      transformControls,
      onPrimarySelected: (obj) => {
        setSelectedObject(obj);
        setSelectedObjectState(obj);
      },
      syncLocalSelection: (primary) => {
        setSelectedObject(primary);
        if (primary) {
          UpdateUI(primary, cachedUIElements);
          syncCameraHelperState(
            syncCameraHelperVisibility,
            showCameraHelper,
            primary.name
          );
          store.dispatch(
            updateActiveComponent({ option: 'Camera', type: 'add' })
          );
        } else {
          transformControls?.detach();
          syncCameraHelperState(syncCameraHelperVisibility, showCameraHelper);
          store.dispatch(selected_object(null));
          syncSelectionOutlineFromStore();
        }
      },
    });
    transformControls.camera = currentCamera;
    return true;
  }

  clearMultiSelection();
  clearLegacyGridObjectListHighlight();
  transformControls.attach(sceneCam);
  transformControls.camera = currentCamera;
  setSelectedObject(sceneCam);
  setSelectedObjectState(sceneCam);
  store.dispatch(selected_object(sceneCam));
  store.dispatch(updateActiveComponent({ option: 'Camera', type: 'add' }));
  syncCameraHelperState(
    syncCameraHelperVisibility,
    showCameraHelper,
    sceneCam.name
  );
  UpdateUI(sceneCam, cachedUIElements);
  syncSelectionOutlineFromStore();
  return true;
}

function handleMultiTogglePick({
  selectionObject,
  topLevel,
  actualLight,
  isLightObject,
  scene,
  transformControls,
  currentCamera,
  setSelectedObject,
  setSelectedObjectState,
  UpdateUI,
  cachedUIElements,
  showCameraHelper,
  syncCameraHelperVisibility,
}) {
  if (!isSceneCameraOrPoint(topLevel)) {
    syncCameraHelperState(syncCameraHelperVisibility, showCameraHelper);
  }

  const toggled = toggleSceneObjectSelection({
    scene,
    object: selectionObject,
    transformControls,
    onPrimarySelected: (obj) => {
      setSelectedObject(obj);
      setSelectedObjectState(obj);
    },
    syncLocalSelection: (primary) => {
      setSelectedObject(primary);
      if (primary) {
        UpdateUI(primary, cachedUIElements);
      } else {
        transformControls?.detach();
        store.dispatch(setSelectedLightId(null));
        store.dispatch(selected_object(null));
        syncSelectionOutlineFromStore();
      }
    },
  });

  transformControls.camera = currentCamera;

  const primary = toggled[toggled.length - 1] ?? null;
  if (
    primary &&
    (primary.isLight ||
      primary.type?.includes('Light') ||
      actualLight ||
      isLightObject)
  ) {
    store.dispatch(setSelectedLightId(primary.name));
    store.dispatch(updateActiveComponent({ option: 'Light', type: 'add' }));
  } else {
    store.dispatch(setSelectedLightId(null));
  }
  syncSelectionOutlineFromStore();
}

function handleSinglePick({
  topLevel,
  actualLight,
  isLightObject,
  transformControls,
  currentCamera,
  setSelectedObject,
  setSelectedObjectState,
  selectObjectInScene,
  showCameraHelper,
  syncCameraHelperVisibility,
  UpdateUI,
  cachedUIElements,
  onVideoMeshToggle,
}) {
  if (!isSceneCameraOrPoint(topLevel)) {
    syncCameraHelperState(syncCameraHelperVisibility, showCameraHelper);
  }

  clearLegacyGridObjectListHighlight();

  if (topLevel.url) {
    const optiontohighlight = document.getElementById(topLevel.url);
    if (optiontohighlight) {
      optiontohighlight.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
    }
  }

  if (actualLight || isLightObject) {
    const lightForTransform = actualLight || topLevel;
    store.dispatch(setSelectedLightId(lightForTransform.name));
    store.dispatch(updateActiveComponent({ option: 'Light', type: 'add' }));
    selectObjectInScene(lightForTransform.name);
    setSelectedObjectState(lightForTransform);
  } else {
    store.dispatch(setSelectedLightId(null));
    setSelectedObject(topLevel);
    store.dispatch(selected_object(topLevel));
    setSelectedObjectState(topLevel);
    selectObjectInScene(topLevel);
    transformControls.attach(topLevel);
  }

  transformControls.camera = currentCamera;

  if (topLevel?.name === 'videoMesh') {
    onVideoMeshToggle?.();
  }

  UpdateUI(topLevel, cachedUIElements);
  syncSelectionOutlineFromStore();
}

function handleEmptyPick({
  transformControls,
  showCameraHelper,
  syncCameraHelperVisibility,
  globalShowLightHelpers,
  getSelectedObject,
  setSelectedObject,
  setSelectedObjectState,
  setVideoPlaying,
}) {
  syncCameraHelperState(syncCameraHelperVisibility, showCameraHelper);
  store.dispatch(setSelectedLightId(null));

  if (globalShowLightHelpers) {
    globalShowLightHelpers();
  }

  const selectedObject = getSelectedObject();
  if (selectedObject && selectedObject.deleteType === 'video') {
    const videoEl =
      selectedObject.videoElement ||
      (selectedObject.material?.map?.image &&
      selectedObject.material?.map instanceof Three.VideoTexture
        ? selectedObject.material.map.image
        : null);
    if (videoEl && typeof videoEl.pause === 'function') {
      videoEl.pause();
    }
    setVideoPlaying(false);
  }

  clearLegacyGridObjectListHighlight();
  setSelectedObject(null);
  setSelectedObjectState?.(null);
  transformControls?.detach();
  clearMultiSelection();
  store.dispatch(setMaterialHighlightMeshes([]));
  store.dispatch(selected_object(null));
  syncSelectionOutlineFromStore();
}

function handleViewportObjectPick({
  intersected,
  isMultiToggle,
  getActiveParentForChildSelection,
  scene,
  transformControls,
  currentCamera,
  setSelectedObject,
  setSelectedObjectState,
  selectObjectInScene,
  UpdateUI,
  cachedUIElements,
  showCameraHelper,
  syncCameraHelperVisibility,
  onVideoMeshToggle,
}) {
  if (isSceneCameraPoint(intersected)) {
    return handleCameraPick({
      intersected,
      isMultiToggle,
      scene,
      transformControls,
      currentCamera,
      setSelectedObject,
      setSelectedObjectState,
      UpdateUI,
      cachedUIElements,
      showCameraHelper,
      syncCameraHelperVisibility,
    });
  }

  const allSelectableObjects =
    store.getState().assets_object.allIntersectedObjects;

  const topLevel = resolveSelectionWithChildMode(
    intersected,
    allSelectableObjects,
    getActiveParentForChildSelection?.(),
    scene
  );

  if (!topLevel || isParticleLifeEnded(topLevel)) return false;

  const actualLight = resolveActualLightFromPick(topLevel, scene);
  const isLightObject = isLightPick(topLevel, actualLight);
  const selectionObject = actualLight || topLevel;

  if (isMultiToggle) {
    handleMultiTogglePick({
      selectionObject,
      topLevel,
      actualLight,
      isLightObject,
      scene,
      transformControls,
      currentCamera,
      setSelectedObject,
      setSelectedObjectState,
      UpdateUI,
      cachedUIElements,
      showCameraHelper,
      syncCameraHelperVisibility,
    });
    return true;
  }

  handleSinglePick({
    topLevel,
    actualLight,
    isLightObject,
    transformControls,
    currentCamera,
    setSelectedObject,
    setSelectedObjectState,
    selectObjectInScene,
    showCameraHelper,
    syncCameraHelperVisibility,
    UpdateUI,
    cachedUIElements,
    onVideoMeshToggle,
  });
  return true;
}

/**
 * Factory for the grid viewport mousedown handler (raycast pick + single/multi select).
 */
export function createGridViewportMouseDownHandler({
  getIsPublishing,
  getIsTransformActive,
  renderer,
  mouse,
  raycaster,
  camera,
  scene,
  transformControls,
  getSelectedObject,
  setSelectedObject,
  setSelectedObjectState,
  selectObjectInScene,
  UpdateUI,
  cachedUIElements,
  showCameraHelper,
  syncCameraHelperVisibility,
  currentCamera,
  globalShowLightHelpers,
  getActiveParentForChildSelection,
  getVideoPlaying,
  setVideoPlaying,
  video,
}) {
  return function handleMouseDown(event) {
    if (getIsPublishing?.()) return;
    if (store.getState()?.window?.window_type === 'preview') return;
    // Keep TransformControls on the pivot marker while editing.
    if (hasActivePivotSession()) return;

    // Primary pointer only — mouse LMB, trackpad click, or touch/pen.
    const isTouchLike =
      event.pointerType === 'touch' || event.pointerType === 'pen';
    if (isTouchLike) {
      if (event.isPrimary === false) return;
    } else if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (getIsTransformActive?.()) return;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const allObjects = [
      ...store.getState().assets_object.allIntersectedObjects,
    ];

    const pickedObject = pickViewportObject({
      mouse: mouse.current,
      raycaster: raycaster.current,
      camera,
      allObjects,
      scene,
    });

    if (pickedObject && !transformControls.dragging) {
      handleViewportObjectPick({
        intersected: pickedObject,
        isMultiToggle: event.ctrlKey || event.metaKey,
        getActiveParentForChildSelection,
        scene,
        transformControls,
        currentCamera,
        setSelectedObject,
        setSelectedObjectState,
        selectObjectInScene,
        UpdateUI,
        cachedUIElements,
        showCameraHelper,
        syncCameraHelperVisibility,
        onVideoMeshToggle: () => {
          const nextPlaying = !getVideoPlaying?.();
          setVideoPlaying?.(nextPlaying);
          if (nextPlaying) {
            video?.play?.();
          } else {
            video?.pause?.();
          }
        },
      });
      return;
    }

    if (!transformControls?.dragging) {
      handleEmptyPick({
        transformControls,
        showCameraHelper,
        syncCameraHelperVisibility,
        globalShowLightHelpers,
        getSelectedObject,
        setSelectedObject,
        setSelectedObjectState,
        setVideoPlaying,
      });
    }
  };
}
